import { useState, useEffect } from "react";
import { XCircle, Save, UtensilsCrossed, Receipt } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function CancellationPrintSettingsCard() {
  const { activeRestaurant, refetch } = useRestaurantContext();
  const [printKitchen, setPrintKitchen] = useState(true);
  const [printReceipt, setPrintReceipt] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValues, setInitialValues] = useState({ printKitchen: true, printReceipt: false });

  useEffect(() => {
    if (activeRestaurant) {
      const kitchen = (activeRestaurant as any).print_cancellation_kitchen ?? true;
      const receipt = (activeRestaurant as any).print_cancellation_receipt ?? false;
      setPrintKitchen(kitchen);
      setPrintReceipt(receipt);
      setInitialValues({ printKitchen: kitchen, printReceipt: receipt });
    }
  }, [activeRestaurant]);

  useEffect(() => {
    setHasChanges(
      printKitchen !== initialValues.printKitchen || 
      printReceipt !== initialValues.printReceipt
    );
  }, [printKitchen, printReceipt, initialValues]);

  const handleSave = async () => {
    if (!activeRestaurant) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("restaurants")
        .update({
          print_cancellation_kitchen: printKitchen,
          print_cancellation_receipt: printReceipt,
        })
        .eq("id", activeRestaurant.id);

      if (error) throw error;
      
      setInitialValues({ printKitchen, printReceipt });
      setHasChanges(false);
      refetch?.();
      toast.success("Configurações salvas!");
    } catch (error) {
      console.error("Error saving cancellation settings:", error);
      toast.error("Erro ao salvar configurações");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <XCircle className="h-5 w-5" />
          Impressão de Cancelamentos
        </CardTitle>
        <CardDescription>
          Configure a impressão automática quando itens são cancelados
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label className="text-base flex items-center gap-2">
              <UtensilsCrossed className="h-4 w-4" />
              Imprimir na Cozinha/Bar
            </Label>
            <p className="text-sm text-muted-foreground">
              Envia ticket de cancelamento para o local de produção do item
            </p>
          </div>
          <Switch
            checked={printKitchen}
            onCheckedChange={setPrintKitchen}
          />
        </div>

        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label className="text-base flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Imprimir Comprovante no Caixa
            </Label>
            <p className="text-sm text-muted-foreground">
              Imprime comprovante com espaço para assinatura do responsável
            </p>
          </div>
          <Switch
            checked={printReceipt}
            onCheckedChange={setPrintReceipt}
          />
        </div>

        {hasChanges && (
          <Button onClick={handleSave} disabled={isLoading} className="gap-2">
            <Save className="h-4 w-4" />
            {isLoading ? "Salvando..." : "Salvar Alterações"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
